import React, { useState, useEffect } from "react";
import { decisionsApi } from "../lib/api";
import { toast } from "sonner";
import { Plus, Gavel, Check, CaretDown, CaretRight } from "@phosphor-icons/react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";

const STATUS_BADGES = {
  active:     "bg-emerald-50 text-emerald-700 border border-emerald-200",
  superseded: "bg-slate-100 text-slate-500 border border-slate-200",
  reversed:   "bg-rose-50 text-rose-700 border border-rose-200",
};

function DecisionCard({ decision, onStatusChange }) {
  const [open, setOpen] = useState(false);

  const updateStatus = async (status) => {
    try {
      const { data } = await decisionsApi.update(decision.id, { status });
      onStatusChange(data);
      toast.success("Decision updated");
    } catch { toast.error("Failed to update"); }
  };

  const hasRefs = [
    ...(decision.supporting_learning_ids || []),
    ...(decision.supporting_theory_ids || []),
    ...(decision.supporting_snapshot_ids || []),
    ...(decision.supporting_evidence_ids || []),
  ].length > 0;

  return (
    <article className="border border-slate-200 rounded-sm bg-white overflow-hidden">
      {/* Card header */}
      <div
        className="px-5 py-4 cursor-pointer hover:bg-slate-50 transition-colors"
        onClick={() => setOpen((o) => !o)}
      >
        <div className="flex items-start gap-3">
          <div className="w-7 h-7 rounded bg-indigo-50 border border-indigo-200 flex items-center justify-center shrink-0 mt-0.5">
            <Gavel size={13} weight="fill" className="text-indigo-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${STATUS_BADGES[decision.status]}`}>
                {decision.status.toUpperCase()}
              </span>
              <span className="font-mono text-[10px] text-slate-400">
                {new Date(decision.created_at).toLocaleDateString("en-AU", {
                  day: "numeric", month: "short", year: "numeric",
                })}
              </span>
              <span className={`font-mono text-xs font-semibold ml-auto ${
                decision.confidence >= 80 ? "text-emerald-600" :
                decision.confidence >= 60 ? "text-amber-600" : "text-rose-600"
              }`}>
                {decision.confidence}<span className="text-[10px] font-normal text-slate-400">/100</span>
              </span>
            </div>
            <h3 className="text-sm font-semibold text-slate-900 leading-snug">{decision.title}</h3>
            <p className="text-xs text-slate-600 mt-1 leading-relaxed line-clamp-2">{decision.decision}</p>
          </div>
          <div className="shrink-0">
            {open ? <CaretDown size={13} className="text-slate-400" /> : <CaretRight size={13} className="text-slate-400" />}
          </div>
        </div>

        {/* Tags */}
        {decision.tags?.length > 0 && (
          <div className="flex gap-1.5 mt-2.5 ml-10 flex-wrap">
            {decision.tags.map((t) => (
              <span key={t} className="text-[10px] font-mono uppercase tracking-wider text-slate-400 bg-slate-50 border border-slate-200 px-1.5 py-0.5 rounded">
                {t}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Expanded body */}
      {open && (
        <div className="border-t border-slate-100 px-5 py-4 space-y-4">
          {/* Rationale */}
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-1.5">
              Rationale
            </div>
            <p className="text-xs text-slate-700 leading-relaxed whitespace-pre-line">{decision.rationale}</p>
          </div>

          {/* Supporting intelligence */}
          {hasRefs && (
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-1.5">
                Supporting Intelligence
              </div>
              <div className="space-y-1">
                {(decision.supporting_learning_ids || []).map((id) => (
                  <div key={id} className="flex items-center gap-2">
                    <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded">LEARNING</span>
                    <span className="font-mono text-[10px] text-slate-500 truncate">{id}</span>
                  </div>
                ))}
                {(decision.supporting_theory_ids || []).map((id) => (
                  <div key={id} className="flex items-center gap-2">
                    <span className="text-[10px] font-semibold text-purple-700 bg-purple-50 border border-purple-200 px-1.5 py-0.5 rounded">THEORY</span>
                    <span className="font-mono text-[10px] text-slate-500 truncate">{id}</span>
                  </div>
                ))}
                {(decision.supporting_snapshot_ids || []).map((id) => (
                  <div key={id} className="flex items-center gap-2">
                    <span className="text-[10px] font-semibold text-blue-700 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded">INSIGHT</span>
                    <span className="font-mono text-[10px] text-slate-500 truncate">{id}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Status actions */}
          {decision.status === "active" && (
            <div className="flex items-center gap-2 pt-1">
              <button
                onClick={() => updateStatus("superseded")}
                className="text-[10px] text-slate-600 border border-slate-200 px-2.5 py-1 rounded hover:bg-slate-50 transition-colors"
              >
                Mark superseded
              </button>
              <button
                onClick={() => updateStatus("reversed")}
                className="text-[10px] text-rose-600 border border-rose-200 px-2.5 py-1 rounded hover:bg-rose-50 transition-colors"
              >
                Reverse decision
              </button>
            </div>
          )}
        </div>
      )}
    </article>
  );
}

export default function DecisionsPage() {
  const [decisions, setDecisions] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [showForm,  setShowForm]  = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [form, setForm] = useState({
    title: "", decision: "", rationale: "", confidence: 80, tags: "",
    supporting_learning_ids: [], supporting_snapshot_ids: [],
  });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = statusFilter ? { status: statusFilter } : {};
      const { data } = await decisionsApi.list(params);
      setDecisions(data);
    } catch { toast.error("Failed to load decisions"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [statusFilter]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.decision.trim() || !form.rationale.trim()) return;
    setSaving(true);
    try {
      const { data } = await decisionsApi.create({
        ...form,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      });
      setDecisions((d) => [data, ...d]);
      setForm({ title: "", decision: "", rationale: "", confidence: 80, tags: "", supporting_learning_ids: [], supporting_snapshot_ids: [] });
      setShowForm(false);
      toast.success("Decision recorded");
    } catch { toast.error("Failed to save decision"); }
    finally { setSaving(false); }
  };

  const updateDecision = (updated) => {
    setDecisions((d) => d.map((x) => x.id === updated.id ? updated : x));
  };

  const filtered = statusFilter ? decisions.filter((d) => d.status === statusFilter) : decisions;

  return (
    <div className="max-w-3xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-1">
            Decisions
          </div>
          <h1 className="text-xl font-bold text-slate-900">
            Strategic Decisions ({decisions.length})
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Deliberate strategic choices supported by intelligence. Preserves organizational reasoning: what was decided, why, and what knowledge supported it.
          </p>
        </div>
        <button
          onClick={() => setShowForm((s) => !s)}
          className="flex items-center gap-1.5 text-xs font-semibold text-white bg-slate-900 px-3 py-2 rounded hover:bg-slate-700 transition-colors shrink-0 ml-4"
        >
          <Plus size={13} />
          Record decision
        </button>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1 mb-6">
        {[
          { value: "",           label: "All" },
          { value: "active",     label: "Active" },
          { value: "superseded", label: "Superseded" },
          { value: "reversed",   label: "Reversed" },
        ].map((f) => (
          <button
            key={f.value}
            onClick={() => setStatusFilter(f.value)}
            className={`px-2.5 py-1 text-xs rounded font-medium transition-colors ${
              statusFilter === f.value ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* New decision form */}
      {showForm && (
        <form onSubmit={submit} className="mb-6 border border-slate-200 rounded-md p-5 bg-white space-y-4">
          <div className="text-xs font-semibold text-slate-700 mb-2">Record a Decision</div>
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 block mb-1.5">Title</label>
            <input
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="Short, descriptive title"
              className="w-full text-xs border border-slate-200 rounded px-2.5 py-2 focus:outline-none focus:ring-1 focus:ring-slate-400"
              required
            />
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 block mb-1.5">Decision</label>
            <textarea
              value={form.decision}
              onChange={(e) => setForm((f) => ({ ...f, decision: e.target.value }))}
              placeholder="What was decided? State the choice clearly."
              rows={2}
              className="w-full text-xs border border-slate-200 rounded px-2.5 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-slate-400"
              required
            />
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 block mb-1.5">Rationale</label>
            <textarea
              value={form.rationale}
              onChange={(e) => setForm((f) => ({ ...f, rationale: e.target.value }))}
              placeholder="Why was this decision made? What constraints, signals, or opportunities drove it?"
              rows={3}
              className="w-full text-xs border border-slate-200 rounded px-2.5 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-slate-400"
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 block mb-1.5">Confidence (0–100)</label>
              <input
                type="number" min="0" max="100"
                value={form.confidence}
                onChange={(e) => setForm((f) => ({ ...f, confidence: Number(e.target.value) }))}
                className="w-full font-mono text-xs border border-slate-200 rounded px-2.5 py-2 focus:outline-none focus:ring-1 focus:ring-slate-400"
              />
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 block mb-1.5">Tags (comma-separated)</label>
              <input
                value={form.tags}
                onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))}
                placeholder="fleet, charging, procurement"
                className="w-full text-xs border border-slate-200 rounded px-2.5 py-2 focus:outline-none focus:ring-1 focus:ring-slate-400"
              />
            </div>
          </div>
          <div className="flex items-center gap-2 pt-1">
            <button type="button" onClick={() => setShowForm(false)} className="text-xs text-slate-500 hover:text-slate-700">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="ml-auto flex items-center gap-1.5 text-xs font-semibold text-white bg-indigo-700 px-3 py-1.5 rounded disabled:opacity-40 hover:bg-indigo-800 transition-colors"
            >
              <Check size={12} />
              Record decision
            </button>
          </div>
        </form>
      )}

      {/* List */}
      {loading ? (
        <div className="text-xs text-slate-400 py-12 text-center">Loading decisions…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16">
          <Gavel size={28} className="text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500">No decisions recorded yet.</p>
          <p className="text-xs text-slate-400 mt-1">Record strategic decisions backed by intelligence.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((d) => (
            <DecisionCard key={d.id} decision={d} onStatusChange={updateDecision} />
          ))}
        </div>
      )}
    </div>
  );
}
